import { FiftyFiftyProps } from 'lib/components/FiftyFifty/FiftyFifty'
import { NarrativeHeroProps } from 'lib/components/NarrativeHero/NarrativeHero'

export interface GuaranteePageContent {
  hero: NarrativeHeroProps
  fifties: FiftyFiftyProps[]
  header: string
  subHeader: string
}

const BACKGROUND_IMAGE = '/images/shipsticks/hero-background-5.jpg'

const hero: NarrativeHeroProps = {
  backgroundImage: BACKGROUND_IMAGE,
  header: 'The ShipSticks Guarantee',
  subHeader: 'Golf is hard enough. Getting there shouldn’t be.',
}

interface FiftyFiftyTextFields {
  header: string
  subHeader: string
  image: string
  body: React.ReactNode
}

const commonFiftyFiftyProps = {
  backgroundColor: 'grey' as 'white' | 'grey',
  showEyebrow: false,
}

const fiftyTexts: FiftyFiftyTextFields[] = [
  {
    header: 'On-Time',
    subHeader: 'You’re not just shipping a bag. You’re protecting your trip.',
    image: '/images/shipsticks/guarantee-1.jpg',
    body: (
      <>
        <p>
          Our powerful network outperforms even the biggest logistics companies, resulting in the
          industry’s best on-time rates. Your golf clubs are guaranteed to arrive where you need
          them, when you need them there.
        </p>
      </>
    ),
  },
  {
    header: 'Easy',
    subHeader: 'It’s golf travel, down to a science.',
    image: '/images/shipsticks/guarantee-2.jpg',
    body: (
      <>
        <p>
          From booking to delivery, every step is designed for convenience. No lugging, no waiting,
          no guesswork - just a seamless way to get your clubs where they need to go, without
          lifting a finger.
        </p>
      </>
    ),
  },
  {
    header: 'Insured',
    subHeader: 'Your gear travels better than you do.',
    image: '/images/shipsticks/guarantee-3.jpg',
    body: (
      <>
        <p>
          For added peace of mind, every shipment comes with complimentary coverage with the option
          to insure for more. Plus, you can stay informed with anytime access to real-time tracking,
          along with delivery notifications right to your inbox or by text.
        </p>
      </>
    ),
  },
  {
    header: 'Flexible',
    subHeader: 'Your golf trip, your way.',
    image: '/images/shipsticks/guarantee-4.jpg',
    body: (
      <>
        <p>
          Have your clubs picked up from your home or office, or from your hotel or pro shop at the
          end of your trip. Have a change of plans? Want to squeeze in another day of golf? No
          problem. Easily adjust your booking online or over the phone.
        </p>
      </>
    ),
  },
  {
    header: 'Expert',
    subHeader: 'A golf club shipper that actually knows a draw from a fade.',
    image: '/images/shipsticks/guarantee-5.jpg',
    body: (
      <>
        <p>
          We don’t do long hold times or confusing call trees. Our dedicated support team is
          available seven days a week (yes, even on weekends) to make sure you get the answers you
          need.
        </p>
      </>
    ),
  },
  {
    header: 'Versatile',
    subHeader: 'We go wherever your game takes you.',
    image: '/images/shipsticks/guarantee-6.jpg',
    body: (
      <>
        <p>
          We service 180 countries and territories, and handle all the tricky customs paperwork. The
          days of dragging your travel bag through unfamiliar cities, packed subways, and endless
          customs lines? Long gone.
        </p>
      </>
    ),
  },
  {
    header: 'Trusted',
    subHeader: 'By tour pros, your weekend foursome, and beyond.',
    image: '/images/shipsticks/guarantee-7.jpg',
    body: (
      <>
        <p>
          We’ve spent over a decade working closely with major carriers like FedEx and UPS to ensure
          your clubs are transported safely and reliably. The result? Millions of happy golfers and
          thousands of partnerships with the game’s most iconic destinations.
        </p>
      </>
    ),
  },
]

const fifties: FiftyFiftyProps[] = fiftyTexts.map((text, index) => ({
  ...text,
  image: {
    src: text.image,
    alt: text.header,
  },
  layout: index % 2 === 0 ? 'image-right' : 'image-left',
  ...commonFiftyFiftyProps,
}))

const header: string = `
  We’re here to make golf travel easier, more reliable, and way less stressful.
  That’s why every shipment is backed by the ShipSticks guarantee.
`

const subHeader: string = `Here’s what that means for you:`

const content: GuaranteePageContent = {
  hero,
  fifties,
  header,
  subHeader,
}

export default content
